export interface AuthenticatedUser {
  id: number;
  username: string;
  email: string;
  created_at: number;
}

export interface Session {
  backend_url: string;
  token: string;
  token_type: string;
  expires_at: number;
  user: AuthenticatedUser;
}

export type AuthMode = 'login' | 'register';

const SESSION_STORAGE_KEY = 'jvmsense3.web.session';
const BACKEND_STORAGE_KEY = 'jvmsense3.web.backend';
const DEFAULT_BACKEND_URL = import.meta.env.VITE_DEFAULT_BACKEND_URL ?? 'https://api.huzuni.dev';

function parseJsonSafe(raw: string): unknown {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function extractErrorMessage(status: number, payload: unknown, raw: string): string {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    payload.error &&
    typeof payload.error === 'object' &&
    'message' in payload.error
  ) {
    return `请求失败 ${status}: ${String(payload.error.message)}`;
  }

  const trimmed = raw.trim();
  if (trimmed) {
    return `请求失败 ${status}: ${trimmed}`;
  }

  return `请求失败 ${status}`;
}

export function normalizeBackendUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  return trimmed.replace(/\/+$/, '');
}

function normalizeSession(session: Session): Session {
  return {
    backend_url: normalizeBackendUrl(String(session.backend_url || DEFAULT_BACKEND_URL)),
    token: String(session.token),
    token_type: String(session.token_type || 'Bearer'),
    expires_at: Number(session.expires_at),
    user: {
      id: Number(session.user.id),
      username: String(session.user.username),
      email: String(session.user.email),
      created_at: Number(session.user.created_at),
    },
  };
}

export function getDefaultBackendUrl(): string {
  return DEFAULT_BACKEND_URL;
}

export function loadStoredBackendUrl(): string {
  if (typeof localStorage === 'undefined') {
    return DEFAULT_BACKEND_URL;
  }

  const stored = localStorage.getItem(BACKEND_STORAGE_KEY);
  return normalizeBackendUrl(stored || DEFAULT_BACKEND_URL);
}

export function saveBackendUrl(backendUrl: string): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const normalized = normalizeBackendUrl(backendUrl) || DEFAULT_BACKEND_URL;
  localStorage.setItem(BACKEND_STORAGE_KEY, normalized);
}

export function loadStoredSession(): Session | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = normalizeSession(JSON.parse(raw) as Session);
    if (parsed.expires_at <= Math.floor(Date.now() / 1000)) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }
}

export function persistSession(session: Session | null): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  if (!session) {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }

  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(normalizeSession(session)));
}

export function clearSession(): void {
  persistSession(null);
}

async function requestAuth<T>(backendUrl: string, path: string, body: unknown): Promise<T> {
  const response = await fetch(`${normalizeBackendUrl(backendUrl)}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  const payload = parseJsonSafe(text);

  if (!response.ok) {
    throw new Error(extractErrorMessage(response.status, payload, text));
  }

  return payload as T;
}

export async function login(
  backendUrl: string,
  identifier: string,
  password: string,
): Promise<Session> {
  const session = await requestAuth<Session>(backendUrl, '/api/auth/login', {
    identifier: identifier.trim(),
    password,
  });

  return normalizeSession(session);
}

export async function register(
  backendUrl: string,
  username: string,
  email: string,
  password: string,
): Promise<Session> {
  const session = await requestAuth<Session>(backendUrl, '/api/auth/register', {
    username: username.trim(),
    email: email.trim(),
    password,
  });

  return normalizeSession(session);
}
